import React, { useState, useEffect } from "react";
import { ScrollView, StyleSheet, TouchableOpacity, View, ActivityIndicator } from "react-native";
import { ArrowSquareOut, Eye } from "@phosphor-icons/react";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";

import type { RootStackParamList } from "../../../navigation/AppNavigator.web";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import Pill from "../../../components/common/Pill/Pill";
import SortingDropdown from "../../../components/common/TableControls/SortingDropdown/SortingDropdown";
import {
  UserUnifiedSubmission,
  useGetUnifiedSubmissionsByFacultyByUserQuery,
} from "../../../graphql/generated/graphql";
import usePagination from "../../../hooks/usePagination";
import AdminTablePagination from "../AdminTable/AdminTablePagination";

interface Student {
  id: string;
}

interface SubmissionsAndGradesTabProps {
  student: Student;
  integration_id: string;
}

const filterItems = [
  { label: "All Types", value: "All" },
  { label: "Assignments", value: "Assignment" },
  { label: "Assessments", value: "Assessment" },
];

const DEFAULT_PAGE_SIZE = 10;

const SubmissionsAndGradesTab: React.FC<SubmissionsAndGradesTabProps> = ({ student, integration_id }) => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const [filterType, setFilterType] = useState<string>("All");
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: DEFAULT_PAGE_SIZE });

  const {
    data: submissionsData,
    loading: submissionsLoading,
    refetch: refetchSubmissions,
  } = useGetUnifiedSubmissionsByFacultyByUserQuery({
    variables: {
      input: {
        userId: student.id,
        page: page,
        pageSize: pageSize,
        submissionType: filterType,
      },
    },
    skip: !student.id,
  });

  useEffect(() => {
    setPage(1);
  }, [filterType]);

  useEffect(() => {
    refetchSubmissions();
  }, [filterType, page, pageSize]);

  const allSubmissionsCount = submissionsData?.getUnifiedSubmissionsByFacultyByUser?.data?.totalCount || 0;

  const paginatedSubmissions = submissionsData?.getUnifiedSubmissionsByFacultyByUser?.data?.submissions || [];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "Assignment":
        return { backgroundColor: "#e0f2fe", textColor: "#0369a1" };
      case "Assessment":
        return { backgroundColor: "#f3e8ff", textColor: "#7c3aed" };
      default:
        return { backgroundColor: "#f3f4f6", textColor: "#6b7280" };
    }
  };

  const getGradeStyle = (grade?: number, isCompleted?: boolean, isAssignment?: boolean) => {
    if (grade === undefined || grade === null) {
      if (isAssignment && !isCompleted) {
        return { backgroundColor: "#FEF3C7" }; // Pending - yellow
      }
      return { backgroundColor: "transparent" };
    }

    if (grade >= 90) {
      return { backgroundColor: "#D1FAE5" }; // Excellent - green (90-100)
    } else if (grade >= 80) {
      return { backgroundColor: "#DBEAFE" }; // Good - blue (80-89)
    } else if (grade >= 70) {
      return { backgroundColor: "#FEF3C7" }; // Average - yellow (70-79)
    } else {
      return { backgroundColor: "#FEE2E2" }; // Poor - red (below 70)
    }
  };

  const getGradeTextColor = (grade?: number, isCompleted?: boolean, isAssignment?: boolean) => {
    if (!isCompleted || grade === undefined || grade === null) {
      if (isAssignment) {
        return { color: "#92400E" }; // Pending - yellow text
      }
      return { color: "#2D2F36" };
    }

    if (grade >= 90) {
      return { color: "#065F46" }; // Excellent - green text (90-100)
    } else if (grade >= 80) {
      return { color: "#1E40AF" }; // Good - blue text (80-89)
    } else if (grade >= 70) {
      return { color: "#92400E" }; // Average - yellow text (70-79)
    } else {
      return { color: "#991B1B" }; // Poor - red text (below 70)
    }
  };

  const formatGrade = (submission: UserUnifiedSubmission) => {
    if (submission.grade !== undefined && submission.grade !== null) {
      return `${submission.grade}/${submission.maximumPoints}`;
    }

    return submission.submissionType === "Assignment" ? "pending" : "—";
  };

  const handleViewSubmission = (submissionId?: string) => {
    if (submissionId) {
      navigation.navigate("Admin Grading Center", { submissionId });
    } else {
      navigation.navigate("Admin Grading Center");
    }
  };

  const handleCourseClick = (courseId: string) => {
    navigation.navigate("Custom Course Student", {
      courseId: courseId,
      integration_id: integration_id,
    });
  };

  if (submissionsLoading) {
    return (
      <View style={styles.contentSection}>
        <View style={styles.sectionCard}>
          <View style={styles.sectionHeader}>
            <CustomText text="recent_submissions" size="l" weight="bold" />
            <View style={styles.filterContainer}>
              <SortingDropdown
                sortBy={filterType}
                setSortBy={setFilterType}
                sortDropdownOpen={filterDropdownOpen}
                setSortDropdownOpen={setFilterDropdownOpen}
                sortItems={filterItems}
              />
            </View>
          </View>
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.contentSection}>
      <View style={styles.sectionCard}>
        <View style={styles.sectionHeader}>
          <CustomText text="recent_submissions" size="l" weight="bold" />
          <View style={styles.filterContainer}>
            <SortingDropdown
              sortBy={filterType}
              setSortBy={setFilterType}
              sortDropdownOpen={filterDropdownOpen}
              setSortDropdownOpen={setFilterDropdownOpen}
              sortItems={filterItems}
            />
          </View>
        </View>

        <ScrollView style={styles.tableScrollWrapper} showsVerticalScrollIndicator={false} tabIndex={0}>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeader}>
              <View style={[styles.cell, styles.dateCell]}>
                <CustomText text="date" size="s" weight="bold" style={styles.headerText} />
              </View>
              <View style={[styles.cell, styles.typeCell]}>
                <CustomText text="type" size="s" weight="bold" style={styles.headerText} />
              </View>
              <View style={[styles.cell, styles.titleCell]}>
                <CustomText text="Module" size="s" weight="bold" style={styles.headerText} />
              </View>
              <View style={[styles.cell, styles.courseCell]}>
                <CustomText text="course" size="s" weight="bold" style={styles.headerText} />
              </View>
              <View style={[styles.cell, styles.gradeCell]}>
                <CustomText text="grade" size="s" weight="bold" style={styles.headerText} />
              </View>
              <View style={[styles.cell, styles.actionsCell]}>
                <CustomText text="actions" size="s" weight="bold" style={styles.headerText} />
              </View>
            </View>

            {paginatedSubmissions.map((submission) => {
              const typeColors = getTypeColor(submission.submissionType);
              const gradeDisplay = formatGrade(submission);
              const gradeColors = getGradeStyle(
                submission.grade,
                submission.status === "Completed",
                submission.submissionType === "Assignment"
              );
              const gradeTextColors = getGradeTextColor(
                submission.grade,
                submission.status === "Completed",
                submission.submissionType === "Assignment"
              );

              return (
                <View key={submission.id} style={styles.tableRow}>
                  <View style={[styles.cell, styles.dateCell]}>
                    <CustomText
                      text={formatDate(submission.created)}
                      size="s"
                      style={styles.dateText}
                      useTranslationText={false}
                    />
                  </View>
                  <View style={[styles.cell, styles.typeCell]}>
                    <Pill
                      text={submission.submissionType}
                      backgroundColor={typeColors.backgroundColor}
                      textColor={typeColors.textColor}
                    />
                  </View>
                  <View style={[styles.cell, styles.titleCell]}>
                    <View style={styles.submissionTitle}>
                      <CustomText text={submission.moduleName} size="s" useTranslationText={false} />
                    </View>
                  </View>
                  <View style={[styles.cell, styles.courseCell]}>
                    <View style={styles.courseCellContent}>
                      <TouchableOpacity
                        style={styles.courseNameContainer}
                        onPress={() => handleCourseClick(submission.courseId)}>
                        <CustomText
                          text={submission.courseName}
                          size="s"
                          style={styles.courseName}
                          useTranslationText={false}
                        />
                        <ArrowSquareOut size={16} color="#3E68FE" />
                      </TouchableOpacity>
                      {submission.courseCode && (
                        <CustomText
                          text={submission.courseCode}
                          size="xs"
                          style={styles.courseCode}
                          useTranslationText={false}
                        />
                      )}
                    </View>
                  </View>
                  <View style={[styles.cell, styles.gradeCell]}>
                    <View style={[styles.grade, gradeColors]}>
                      <CustomText
                        text={gradeDisplay}
                        size="xs"
                        weight="bold"
                        style={[styles.gradeText, gradeTextColors]}
                        useTranslationText={false}
                      />
                    </View>
                  </View>
                  <View style={[styles.cell, styles.actionsCell]}>
                    <CustomButton
                      text="View"
                      leftIcon={<Eye size={14} color="#3E68FE" />}
                      onPress={() => handleViewSubmission(submission.id)}
                      size="s"
                      styleType="lightStroke"
                      textStyle={{ color: "#3E68FE" }}
                    />
                  </View>
                </View>
              );
            })}
            {paginatedSubmissions.length > 0 && (
              <AdminTablePagination
                currentPage={page}
                totalPages={Math.ceil(allSubmissionsCount / pageSize)}
                totalItems={allSubmissionsCount}
                itemsPerPage={pageSize}
                onPageChange={setPage}
                onItemsPerPageChange={(newPageSize) => {
                  setPageSize(newPageSize);
                  setPage(1);
                }}
              />
            )}
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  contentSection: {
    flex: 1,
  },
  sectionCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    overflow: "hidden",
  },
  sectionHeader: {
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    overflow: "visible",
    zIndex: 10,
  },
  filterContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  tableScrollWrapper: {
    backgroundColor: "#FFFFFF",
  },
  tableContainer: {
    backgroundColor: "#FFFFFF",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  cell: {
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  dateCell: {
    flex: 1.5,
  },
  typeCell: {
    flex: 1.5,
    alignItems: "flex-start",
  },
  titleCell: {
    flex: 2.5,
  },
  courseCell: {
    flex: 2,
  },
  gradeCell: {
    flex: 1,
    alignItems: "flex-start",
  },
  actionsCell: {
    flex: 1.5,
    alignItems: "flex-start",
  },
  headerText: {
    color: "#475569",
  },
  dateText: {
    color: "#111827",
  },
  submissionTitle: {
    flexDirection: "column",
    gap: 4,
  },
  courseCellContent: {
    flexDirection: "column",
    gap: 4,
  },
  courseNameContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  courseName: {
    color: "#3E68FE",
  },
  courseCode: {
    color: "#6B7280",
    fontFamily: "monospace",
  },
  grade: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 12,
    minWidth: 50,
    alignItems: "center",
  },
  gradeText: {
    color: "#111827",
    textAlign: "center",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
});

export default SubmissionsAndGradesTab;
